import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { 
  Building2, 
  User, 
  Phone, 
  Mail, 
  MapPin,
  Trophy,
  Calendar,
  Users,
  Briefcase,
  ArrowLeft,
  Send
} from "lucide-react";

const ServiceRequest = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    // Section A: Client Contact Details (Always Required)
    fullName: "",
    companyName: "",
    position: "",
    email: "",
    phone: "",
    whatsapp: "",
    address: "",
    city: "",
    country: "",
    
    // Service Selection
    serviceType: "",
    otherService: "",
    
    // Service Details
    projectDescription: "",
    timeline: "",
    budget: "",
    urgency: "",
    
    // Additional Requirements
    additionalServices: [],
    specialRequirements: "",
    
    // Communication Preferences
    preferredContact: "",
    meetingPreference: "",
    
    // Agreement
    termsAccepted: false
  });

  const serviceTypes = [
    "Event Management (Full Service)",
    "Event Permits & Licensing Support",
    "Event Ticketing & Accreditation Setup",
    "Event Security & Cleaning Planning",
    "Sports Management Consultancy",
    "Sports Club NOC Support (UAE Councils)",
    "Sports Federation NOC Assistance", 
    "HR & Compliance Services (Full Package)",
    "HR Policies & Procedures (UAE-compliant)",
    "Organization Structure & Job Grading",
    "Appraisal System Development",
    "HR Forms & Templates Creation",
    "Onboarding & Offboarding Systems",
    "Corporate Training",
    "Business Consulting",
    "Marketing & Branding",
    "Legal Advisory",
    "Financial Planning",
    "Other (Please Specify)"
  ];

  const urgencyLevels = [
    "Urgent (Within 1 week)",
    "High (Within 2 weeks)", 
    "Medium (Within 1 month)",
    "Low (Flexible timeline)"
  ];

  const budgetRanges = [
    "Under AED 5,000",
    "AED 5,000 - 15,000",
    "AED 15,000 - 50,000", 
    "AED 50,000 - 100,000",
    "Above AED 100,000",
    "To be discussed"
  ];

  const additionalServiceOptions = [
    "Project Management",
    "Event Permit Processing",
    "Food & Beverage Permits",
    "Parking & Traffic Permits",
    "UAE Sports Council NOC Support",
    "Sports Federation NOC Processing",
    "UAE Labor Law Compliance Review",
    "Salary Benchmarking & Analysis",
    "Employee Handbook Creation",
    "Performance Management Templates",
    "Contract Templates (Arabic & English)",
    "Visa & Immigration Support",
    "Security Personnel Coordination",
    "Cleaning & Maintenance Planning",
    "Documentation & Reporting",
    "Follow-up Support",
    "Training & Development",
    "Ongoing Consultation"
  ];

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAdditionalServiceChange = (service: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      additionalServices: checked 
        ? [...prev.additionalServices, service]
        : prev.additionalServices.filter(s => s !== service)
    }));
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.fullName || !formData.email || !formData.phone) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required contact details.",
        variant: "destructive"
      });
      return;
    }

    if (!formData.serviceType) {
      toast({
        title: "Service Required",
        description: "Please select the service you're interested in.",
        variant: "destructive"
      });
      return;
    }

    if (!formData.termsAccepted) {
      toast({
        title: "Terms & Conditions",
        description: "Please accept the terms and conditions to proceed.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare data for backend submission
      const submissionData = {
        // Basic client information
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        company_name: formData.companyName,
        application_type: 'service_request',
        service_type: formData.serviceType,
        
        // Service request specific fields
        project_description: formData.projectDescription,
        timeline: formData.timeline,
        budget_range: formData.budget,
        additional_requirements: formData.additionalRequirements,
        communication_preference: formData.preferredCommunication,
        additional_services: formData.additionalServices,
        
        // Contact information
        contact_address: formData.address,
        contact_city: formData.city,
        contact_country: formData.country || 'UAE'
      };

      // Submit to Supabase Edge Function
      const response = await fetch('https://axlxwpuaqgbfkoampsai.supabase.co/functions/v1/handle_client_applications_20251222_1300_v2', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImF4bHh3cHVhcWdiZmtvYW1wc2FpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY0MDY3MDgsImV4cCI6MjA4MTk4MjcwOH0.kJ0f10vouM1ruwI2mwZ9tuAU_9df4l07Vzf1tE_tS6w',
          'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImF4bHh3cHVhcWdiZmtvYW1wc2FpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY0MDY3MDgsImV4cCI6MjA4MTk4MjcwOH0.kJ0f10vouM1ruwI2mwZ9tuAU_9df4l07Vzf1tE_tS6w',
        },
        body: JSON.stringify(submissionData)
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: "Request Submitted Successfully!",
          description: "We will contact you within 24 hours to discuss your requirements. Request ID: " + result.client_id?.slice(0, 8),
        });
        
        // Reset form
        setFormData({
          fullName: "",
          companyName: "",
          position: "",
          email: "",
          phone: "",
          whatsapp: "",
          address: "",
          city: "",
          country: "",
          serviceType: "",
          otherService: "",
          projectDescription: "",
          timeline: "",
          budget: "",
          urgency: "",
          additionalRequirements: "",
          additionalServices: [],
          preferredCommunication: "",
          termsAccepted: false
        });
      } else {
        throw new Error('Failed to submit request');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast({
        title: "Submission Failed",
        description: "There was an error submitting your request. Please try again or contact us directly at info@eliteedgemc.ae",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="./images/logo.jpg" 
                alt="Company Logo" 
                className="h-12 w-auto object-contain"
              />
              <span className="text-3xl font-bold text-primary">Elite Edge Management Consultancy</span>
            </Link>
            <div className="hidden md:flex items-center space-x-6">
              <Link to="/" className="text-foreground hover:text-primary transition-colors">Home</Link>
              <Link to="/services" className="text-foreground hover:text-primary transition-colors">Services</Link>
              <Link to="/login" className="text-foreground hover:text-primary transition-colors">Admin</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="py-12 px-4 bg-gradient-to-br from-primary/5 via-primary/10 to-accent/5">
        <div className="container mx-auto text-center">
          <Badge className="mb-4" variant="secondary">Service Request Form</Badge>
          <h1 className="text-5xl font-bold mb-4">Request Our Services</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Tell us about your project requirements and we'll provide you with a customized solution
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-4xl">
          <form onSubmit={handleSubmit} className="space-y-8">
            
            {/* Section A: Client Contact Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <User className="h-5 w-5 mr-2" />
                  Section A: Client Contact & Communication Details
                </CardTitle>
                <CardDescription>
                  Please provide your complete contact information (All fields required)
                </CardDescription>
              </CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name *</Label>
                  <Input
                    id="fullName"
                    value={formData.fullName}
                    onChange={(e) => handleInputChange("fullName", e.target.value)}
                    placeholder="Enter your full name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="companyName">Company Name</Label>
                  <Input
                    id="companyName"
                    value={formData.companyName}
                    onChange={(e) => handleInputChange("companyName", e.target.value)}
                    placeholder="Enter company name (if applicable)"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="position">Position/Title</Label>
                  <Input
                    id="position"
                    value={formData.position}
                    onChange={(e) => handleInputChange("position", e.target.value)}
                    placeholder="Your position or title"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder="your.email@example.com"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    placeholder="+971 XX XXX XXXX"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="whatsapp">WhatsApp Number</Label>
                  <Input
                    id="whatsapp"
                    value={formData.whatsapp}
                    onChange={(e) => handleInputChange("whatsapp", e.target.value)}
                    placeholder="+971 XX XXX XXXX"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => handleInputChange("address", e.target.value)}
                    placeholder="Complete address"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => handleInputChange("city", e.target.value)}
                    placeholder="City"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => handleInputChange("country", e.target.value)}
                    placeholder="Country"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Service Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Briefcase className="h-5 w-5 mr-2" />
                  Service Requirements
                </CardTitle>
                <CardDescription>
                  Select the service you're interested in and provide project details
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="serviceType">Service Type *</Label>
                  <Select value={formData.serviceType} onValueChange={(value) => handleInputChange("serviceType", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select the service you need" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceTypes.map((service) => (
                        <SelectItem key={service} value={service}>{service}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.serviceType === "Other (Please Specify)" && (
                  <div className="space-y-2">
                    <Label htmlFor="otherService">Please Specify Service</Label>
                    <Input
                      id="otherService"
                      value={formData.otherService}
                      onChange={(e) => handleInputChange("otherService", e.target.value)}
                      placeholder="Describe the service you need"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="projectDescription">Project Description *</Label>
                  <Textarea
                    id="projectDescription"
                    value={formData.projectDescription}
                    onChange={(e) => handleInputChange("projectDescription", e.target.value)}
                    placeholder="Provide detailed description of your project requirements, objectives, and expected outcomes"
                    rows={4}
                    required
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="timeline">Project Timeline</Label>
                    <Input
                      id="timeline"
                      value={formData.timeline}
                      onChange={(e) => handleInputChange("timeline", e.target.value)}
                      placeholder="e.g., 3 months, by March 2024"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="urgency">Urgency Level</Label>
                    <Select value={formData.urgency} onValueChange={(value) => handleInputChange("urgency", value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select urgency level" />
                      </SelectTrigger>
                      <SelectContent>
                        {urgencyLevels.map((level) => (
                          <SelectItem key={level} value={level}>{level}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="budget">Budget Range</Label>
                  <Select value={formData.budget} onValueChange={(value) => handleInputChange("budget", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select your budget range" />
                    </SelectTrigger>
                    <SelectContent>
                      {budgetRanges.map((range) => (
                        <SelectItem key={range} value={range}>{range}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Additional Services */}
            <Card>
              <CardHeader>
                <CardTitle>Additional Services</CardTitle>
                <CardDescription>
                  Select any additional services you might need (Optional)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {additionalServiceOptions.map((service) => (
                    <div key={service} className="flex items-center space-x-2">
                      <Checkbox
                        id={service}
                        checked={formData.additionalServices.includes(service)}
                        onCheckedChange={(checked) => handleAdditionalServiceChange(service, checked as boolean)}
                      />
                      <Label htmlFor={service} className="text-sm font-medium">
                        {service}
                      </Label>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 space-y-2">
                  <Label htmlFor="specialRequirements">Special Requirements or Notes</Label>
                  <Textarea
                    id="specialRequirements"
                    value={formData.specialRequirements}
                    onChange={(e) => handleInputChange("specialRequirements", e.target.value)}
                    placeholder="Any special requirements, constraints, or additional information"
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Communication Preferences */}
            <Card>
              <CardHeader>
                <CardTitle>Communication Preferences</CardTitle>
                <CardDescription>
                  How would you like us to contact you?
                </CardDescription>
              </CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="preferredContact">Preferred Contact Method</Label>
                  <Select value={formData.preferredContact} onValueChange={(value) => handleInputChange("preferredContact", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select preferred contact method" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="phone">Phone Call</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="meeting">In-Person Meeting</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="meetingPreference">Meeting Preference</Label>
                  <Select value={formData.meetingPreference} onValueChange={(value) => handleInputChange("meetingPreference", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select meeting preference" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="office">Visit our office</SelectItem>
                      <SelectItem value="client_location">Visit client location</SelectItem>
                      <SelectItem value="video_call">Video conference</SelectItem>
                      <SelectItem value="phone_call">Phone discussion</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Terms and Submit */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center space-x-2 mb-6">
                  <Checkbox
                    id="terms"
                    checked={formData.termsAccepted}
                    onCheckedChange={(checked) => handleInputChange("termsAccepted", checked as boolean)}
                    required
                  />
                  <Label htmlFor="terms" className="text-sm">
                    I agree to the terms and conditions and consent to Elite Edge Management Consultancy 
                    contacting me regarding this service request. I understand that this is not a binding 
                    commitment and further discussions will determine the final scope and pricing.
                  </Label>
                </div>
                
                <div className="flex gap-4">
                  <Button type="submit" className="bg-gradient-to-r from-primary to-primary-glow" disabled={isSubmitting}>
                    <Send className="h-4 w-4 mr-2" />
                    {isSubmitting ? "Submitting..." : "Submit Service Request"}
                  </Button>
                  <Button type="button" variant="outline" asChild>
                    <Link to="/services">
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back to Services
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ServiceRequest;